/**
 * TransitionContainer
 *
 * Container component that manages transitions between location views.
 * Handles the mounting/unmounting of old and new content during transitions.
 */

import React, { useState, useEffect, useRef } from 'react';
import { CampusLocation } from '../../../types/spatial';
import { useLocationTransition } from '../../../hooks/useLocationTransition';
import { LocationTransition } from './LocationTransition';

export interface TransitionContainerProps {
  /** Current location being displayed */
  currentLocation: CampusLocation;
  /** The content to display */
  children: React.ReactNode;
  /** Optional CSS class name */
  className?: string;
}

interface TransitionState {
  location: CampusLocation;
  content: React.ReactNode;
  isExiting: boolean;
}

export const TransitionContainer: React.FC<TransitionContainerProps> = ({
  currentLocation,
  children,
  className = '',
}) => {
  const {
    getTransitionConfig,
    setTransitioning,
    prefersReducedMotion,
    transitionPreferences,
  } = useLocationTransition();

  const previousLocationRef = useRef<CampusLocation>(currentLocation);
  const [exitingContent, setExitingContent] = useState<TransitionState | null>(null);
  const [isEntering, setIsEntering] = useState(true);

  useEffect(() => {
    const prevLocation = previousLocationRef.current;

    if (prevLocation !== currentLocation) {
      // Location changed - start transition
      const config = getTransitionConfig(prevLocation, currentLocation);

      if (config.type === 'none' || config.duration === 0) {
        // No animation - just switch
        previousLocationRef.current = currentLocation;
        return;
      }

      // Set transitioning state
      setTransitioning(true);

      // Store the exiting content (we need to keep a reference to the old children)
      // Note: In practice, we let the parent handle the old content
      setIsEntering(false);

      // Trigger enter animation after a frame
      requestAnimationFrame(() => {
        setIsEntering(true);
      });

      previousLocationRef.current = currentLocation;
    }
  }, [currentLocation, getTransitionConfig, setTransitioning]);

  // Handle transition end
  const handleTransitionEnd = () => {
    setTransitioning(false);
    setExitingContent(null);
  };

  // Get transition config for current transition
  const config = getTransitionConfig(
    previousLocationRef.current,
    currentLocation
  );

  // If reduced motion or disabled, render without transition wrapper
  if (prefersReducedMotion || !transitionPreferences.enabled) {
    return (
      <div className={`h-full w-full ${className}`}>
        {children}
      </div>
    );
  }

  return (
    <div className={`relative h-full w-full overflow-hidden ${className}`}>
      {/* Exiting content (if any) */}
      {exitingContent && (
        <div className="absolute inset-0">
          <LocationTransition
            type={config.type}
            direction={config.direction}
            duration={config.duration}
            isEntering={false}
            onTransitionEnd={() => setExitingContent(null)}
          >
            {exitingContent.content}
          </LocationTransition>
        </div>
      )}

      {/* Current content */}
      <LocationTransition
        type={config.type}
        direction={config.direction}
        duration={config.duration}
        isEntering={isEntering}
        onTransitionEnd={handleTransitionEnd}
        className="h-full w-full"
      >
        {children}
      </LocationTransition>
    </div>
  );
};

export default TransitionContainer;
